import { useCallback, useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom/';

import { useQuery } from '../../../../hooks';
import { currencyFormat } from '../../../../services';
import { INSURANCE } from '../../../navigation/routes.constants';
import { getOrcaPolicyCodeByName, getOrcaWellnessByName } from '../utils/orcaChangeCoverage.utils';

export const useChangeCoverageOrcaCa = (productCode) => {
  const query = useQuery();
  const history = useHistory();
  const store = useSelector(({
    billingPayments,
    policies,
  }) => ({ billingPayments, policies }), shallowEqual);

  const { updatePaymentMethodCa } = store.billingPayments;

  const {
    changeCoverageOrcaCanada,
    multibrandPolicies,
    otherPolicyDetail,
  } = store.policies;
  const {
    coverage,
    customer,
    effectiveOn,
    error,
    loading,
    orcaMarketChannelId,
    petId,
    petName,
    previewData,
    products: { baseProducts, wellnessOptions },
    proxyPolicy,
    quoteGuid,
    quotePreview,
    quoteStatusDisplay,
    selectedLimits,
    selectedProductCode,
    selectedWellness,
    submitQuote,
    wellnessOptionsDetails,
  } = changeCoverageOrcaCanada;
  const { selectedLimits: initialSelectedLimits } = coverage;

  const {
    paymentManagementUrl,
    policyId,
    customerId,
  } = updatePaymentMethodCa;

  const defaultPolicy = useMemo(
    () => store.policies?.defaultPolicy
      || otherPolicyDetail, [store.policies?.defaultPolicy, otherPolicyDetail],
  );

  const params = useParams();
  const { marketingChanelId, policyNumber } = params;

  const calculatePrice = useCallback(({
    monthlyPreviewResults = [{
      monthlyInstallment: 0,
      stateFilingProductId: 0,
    }],
    productSelected = {
      stateFilingProductId: 0,
    },
    wellnessSelected = {
      stateFilingProductId: 0,
    },
  }) => {
    const price = monthlyPreviewResults.filter(({ isMandatory }) => isMandatory)
      .reduce((prev, next) => prev + next.monthlyInstallment, 0);
    const productPrice = monthlyPreviewResults.find(({
      stateFilingProductId,
    }) => stateFilingProductId === productSelected.stateFilingProductId);
    const wellnessPrice = wellnessSelected ? monthlyPreviewResults.find(({
      stateFilingProductId,
    }) => wellnessSelected.stateFilingProductId === stateFilingProductId)
      : { monthlyInstallment: 0 };

    return price
      + (productPrice?.monthlyInstallment || 0)
      + (wellnessPrice?.monthlyInstallment || 0);
  }, []);

  const coveragePeriodId = useMemo(() => query.get('coveragePeriodId')
    || params.coveragePeriodId, []);
  const queryPetId = useMemo(() => query.get('petId'), []);

  const selectedPolicy = useMemo(() => {
    if (store.policies.loading) {
      return {};
    }

    return multibrandPolicies.find(({
      PolicyId,
      CoveragePeriodId,
    }) => PolicyId === +policyNumber
      && CoveragePeriodId === coveragePeriodId);
  }, [
    multibrandPolicies,
    coveragePeriodId,
    policyNumber,
    store.policies.loading,
  ]);

  const productName = useMemo(() => {
    if (!defaultPolicy) {
      return {};
    }

    const coverages = defaultPolicy.PetCoverages?.[0] || [];
    const summary = coverages.CoverageSummary;

    return summary?.ProductName;
  }, [defaultPolicy]);

  const selectedPet = useMemo(() => {
    if (!defaultPolicy?.Id) {
      return { petId: 0, petName: '', type: 0 };
    }

    const coverages = defaultPolicy.PetCoverages || [];
    const { Pet } = coverages.find((item) => item.Pet.Id === +queryPetId) || {};

    return {
      petId: Pet?.Id,
      petName: Pet?.Name,
      type: Pet?.Type,
    };
  }, [defaultPolicy, queryPetId]);

  const currentWellness = useMemo(() => {
    if (!defaultPolicy) {
      return null;
    }

    const petCoverage = defaultPolicy.PetCoverages?.[0] || {};

    const currentOption = wellnessOptions?.find((item) => petCoverage.Extras
      ?.find((extra) => {
        const wellnessCode = getOrcaWellnessByName(extra.Name);

        return wellnessCode?.code === item.code;
      }));

    return currentOption ?? null;
  }, [wellnessOptions, defaultPolicy]);

  const previewPrice = useMemo(() => {
    const { monthlyPreviewResults, productSelected } = previewData;

    return calculatePrice({
      monthlyPreviewResults,
      productSelected,
      wellnessSelected: currentWellness,
    });
  }, [currentWellness, previewData, baseProducts]);

  const preview = useMemo(() => {
    const coverageLimit = coverage?.selectedLimits;

    if (!productName) {
      return {
        annualBenefit: {},
        code: '',
        deductible: '',
        display: '',
        premium: {},
        reimbursement: {},
      };
    }

    const productCodeByName = getOrcaPolicyCodeByName(productName);
    const baseProduct = baseProducts
      .find(({ code }) => code === productCodeByName);

    if (!baseProduct) {
      return {
        annualBenefit: {},
        code: '',
        deductible: '',
        display: '',
        premium: {},
        reimbursement: {},
      };
    }

    const { selectedProducts } = coverage;
    const { code, display, limits } = baseProduct;

    const annualBenefit = limits.coverageLimits
      .find(({ id }) => id === coverageLimit.coverageLimitId);
    const premium = selectedProducts.reduce((prev, product) => prev
      + product.preDiscountedMonthlyPremium, 0);
    const deductible = limits.deductibleLimits
      .find(({ id }) => id === coverageLimit.deductibleId);
    const reimbursement = limits.coinsuranceLimits
      .find(({ id }) => id === coverageLimit.coinsuranceId);

    const previewSelected = {
      annualBenefit,
      code,
      coverage,
      deductible,
      display,
      premium,
      reimbursement,
    };

    return {
      AnnualBenefit: previewSelected?.annualBenefit?.display,
      Deductible: previewSelected?.deductible?.display,
      HavePerIncidentCopay: false,
      LastDateToChange: effectiveOn,
      NewPremiumEffectiveDate: effectiveOn,
      PerIncidentCopayInformation: null,
      PetName: selectedPet.petName,
      Premium: currencyFormat({ value: previewPrice }),
      Reimbursement: previewSelected?.reimbursement?.display,
    };
  }, [baseProducts, coverage, previewPrice, selectedPet, selectedPolicy]);

  const powerUps = useMemo(() => {
    if (!defaultPolicy) {
      return [];
    }

    const petCoverage = defaultPolicy.PetCoverages?.[0] || {};

    return wellnessOptions?.map((item) => ({
      detail: wellnessOptionsDetails
        .filter(({
          stateFilingProductId,
        }) => stateFilingProductId === item.stateFilingProductId)
        ?.map(({ preventativeBenefit }) => preventativeBenefit)
        ?.join(', ') || '',
      disabled: true,
      id: item.productId,
      included: !!petCoverage.Extras?.find((extra) => {
        const wellnessCode = getOrcaWellnessByName(extra.Name);

        return wellnessCode.code === item.code;
      }),
      title: item.display,
    }));
  }, [defaultPolicy, wellnessOptions, wellnessOptionsDetails]);

  const wellnessOptionsWithPrice = useMemo(() => {
    const codeSelected = productCode || selectedProductCode;
    const quote = quotePreview[codeSelected];

    if (!quote?.monthlyPreviewResults) {
      return [];
    }

    const options = wellnessOptions.map((option) => ({
      ...option,
      details: wellnessOptionsDetails
        .filter(({
          stateFilingProductId,
        }) => stateFilingProductId === option.stateFilingProductId)
        ?.map(({ preventativeBenefit }) => preventativeBenefit)
        ?.join(', ') || '',
      price: quote.monthlyPreviewResults.find(({
        stateFilingProductId,
      }) => stateFilingProductId === option.stateFilingProductId)
        ?.monthlyInstallment,
    }));

    return options.filter((option) => option.price > 0);
  }, [
    productCode,
    quotePreview,
    selectedProductCode,
    wellnessOptions,
    wellnessOptionsDetails,
  ]);

  useEffect(() => {
    if (!error && !submitQuote && !coveragePeriodId) {
      setTimeout(() => {
        history.replace(INSURANCE);
      }, 2000);
    }
  }, [coveragePeriodId, error, submitQuote]);

  return {
    baseProducts,
    calculatePrice,
    coveragePeriodId,
    currentWellness,
    customer,
    customerId,
    defaultPolicy,
    effectiveOn,
    initialSelectedLimits,
    loading,
    marketingChanelId,
    orcaMarketChannelId,
    paymentManagementUrl,
    petId,
    petName,
    policyId,
    policyNumber,
    powerUps,
    preview,
    productName,
    proxyPolicy,
    queryPetId,
    quoteGuid,
    quotePreview,
    quoteStatusDisplay,
    selectedLimits,
    selectedPet,
    selectedPolicy,
    selectedProductCode,
    selectedWellness,
    submitQuote,
    wellnessOptions: wellnessOptionsWithPrice,
    wellnessOptionsDetails,
  };
};
